import { cn } from "@/lib/utils"

interface PaginationProps {
  page: number
  totalPages?: number
  total: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
  /** Extra classes on the outer bar (e.g. fixed footer strip). */
  className?: string
}

export function Pagination({
  page,
  totalPages: totalPagesProp,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
  className,
}: PaginationProps) {
  if (total === 0) return null

  const totalPages = totalPagesProp ?? Math.max(1, Math.ceil(total / pageSize))
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2",
        className,
      )}
    >
      <div className="flex items-center gap-2 self-center text-xs leading-none text-[#b0b0b0]">
        <span>
          Showing {start}-{end} of {total}
        </span>
        {onPageSizeChange && (
          <>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-7 rounded border border-[#4a4a4a] bg-[#2d2d2d] px-2 text-xs text-[#e0e0e0]"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>per page</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 self-center">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="inline-flex h-7 items-center rounded-md border border-[#676d78] bg-[#4f5561] px-2.5 text-xs font-medium text-[#e0e0e0] shadow-sm transition-colors hover:bg-[#5a616f] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>
        <span className="px-1 text-xs leading-none text-[#b0b0b0]">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="inline-flex h-7 items-center rounded-md border border-[#676d78] bg-[#4f5561] px-2.5 text-xs font-medium text-[#e0e0e0] shadow-sm transition-colors hover:bg-[#5a616f] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  )
}
